import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';
import { Link } from 'react-router-dom';

// ── Layout (flex: main + sidebar) ───────────────────────

export const PageWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 0;
    margin: -${theme.spacing[6]};
    min-height: calc(100vh - ${theme.layout.header});

    ${down('lg')} {
      margin: -${theme.spacing[4]};
    }

    ${down('md')} {
      margin: -${theme.spacing[3]};
    }
  `}
`;

export const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 1.75rem 2rem;

  ${down('sm')} {
    padding: 1.25rem 1rem;
  }
`;

// ── Page Header ─────────────────────────────────────────

export const PageHeader = styled.header`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;

    h1 {
      font-size: 1.375rem;
      font-weight: 800;
      color: ${theme.colors.foreground[100]};
      letter-spacing: -0.5px;
    }
  `}
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const BackButton = styled.button`
  ${({ theme }) => css`
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${theme.colors.brand.primary.default};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: ${theme.transition.default};

    :hover {
      background: ${theme.colors.brand.primary.opaque};
    }
  `}
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.25rem 0.75rem;
  `}
`;

// ── Live Bar ────────────────────────────────────────────

export const LiveBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.75rem;
  padding: 0 2.875rem;
`;

export const LiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #ef4444;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

export const PulseDot = styled.span`
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 2s ease-in-out infinite;
`;

// ── Room Grid (3 colunas) ───────────────────────────────

export const RoomGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-left: 2.5rem;

  ${down('md')} {
    grid-template-columns: 1fr;
  }
`;

// ── Sidebar ─────────────────────────────────────────────

export const Sidebar = styled.aside`
  ${({ theme }) => css`
    width: 420px;
    flex-shrink: 0;
    border-left: 1px solid ${theme.colors.background[40]};
    background: ${theme.colors.background[100]};
    position: sticky;
    top: ${theme.layout.header};
    height: calc(100vh - ${theme.layout.header});
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding: 1.125rem;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: 4px;
    }

    ${down('xl')} {
      display: none;
    }
  `}
`;

export const ScheduleWrapper = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
`;

export const ScheduleFade = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3rem;
    background: linear-gradient(transparent, ${theme.colors.background[100]});
    pointer-events: none;
  `}
`;

// ── Sidebar: Section wrapper ────────────────────────────

export const SidebarSection = styled.section`
  padding: 1.125rem;
`;

export const SidebarSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
`;

export const SidebarSectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const SidebarSectionTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 700;
    color: ${theme.colors.foreground[60]};
    text-transform: uppercase;
    letter-spacing: 0.8px;
  `}
`;

export const SidebarViewAll = styled(Link)`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
    text-decoration: none;
    transition: ${theme.transition.default};

    &:hover {
      opacity: 0.8;
    }
  `}
`;

// ── Sidebar: Live Hero Card ─────────────────────────────

export const LiveHeroWrapper = styled.div`
  ${({ theme }) => css`
    position: relative;
    background: ${theme.colors.background[100]};
    border-radius: 1.25rem;
    padding: 1.25rem;
    overflow: hidden;
    border: 1px solid ${theme.colors.background[40]};
  `}
`;

export const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.3125rem 0.75rem;
  border-radius: 1.25rem;
  background: #ef4444;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 0.875rem;
`;

export const LiveBadgeDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #fff;
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const LiveGlassCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
`;

export const LiveRoomName = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-bottom: 0.625rem;
    border-bottom: 1px solid ${theme.colors.background[40]};

    svg {
      flex-shrink: 0;
      color: ${theme.colors.foreground[40]};
    }
  `}
`;

export const LiveNowPlaying = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const LiveNowPlayingHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const equalizerBounce = keyframes`
  0%, 100% { height: 4px; }
  50% { height: 16px; }
`;

export const EqualizerBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 16px;
`;

export const EqualizerBar = styled.span<{ $delay: number }>`
  width: 3px;
  border-radius: 1.5px;
  background: #22c55e;
  animation: ${equalizerBounce} 0.8s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}s;
  height: 4px;
`;

export const LiveNowLabel = styled.span`
  font-size: 0.5625rem;
  font-weight: 700;
  color: #22c55e;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const LiveSessionTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const LiveSpeakerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const LiveSpeakerAvatars = styled.div`
  display: flex;
  align-items: center;
`;

const SPEAKER_COLORS = ['#4A6CF7', '#22C55E', '#C084FC', '#F59E0B', '#EF4444'];

export const LiveSpeakerAvatar = styled.img<{ $index: number }>`
  ${({ theme, $index }) => css`
    width: 1.625rem;
    height: 1.625rem;
    border-radius: 0.5rem;
    object-fit: cover;
    border: 2px solid ${theme.colors.background[100]};
    margin-left: ${$index > 0 ? '-0.375rem' : '0'};
    position: relative;
    z-index: ${10 - $index};
  `}
`;

export const LiveSpeakerFallback = styled.div<{ $index: number }>`
  ${({ theme, $index }) => css`
    width: 1.625rem;
    height: 1.625rem;
    border-radius: 0.5rem;
    background: ${SPEAKER_COLORS[$index % SPEAKER_COLORS.length]};
    border: 2px solid ${theme.colors.background[100]};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.4375rem;
    font-weight: 700;
    color: #fff;
    margin-left: ${$index > 0 ? '-0.375rem' : '0'};
    position: relative;
    z-index: ${10 - $index};
  `}
`;

export const LiveSpeakerNames = styled.span`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${theme.colors.foreground[40]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  `}
`;

// ── Sidebar: Live Progress Bar ──────────────────────────

export const LiveProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const LiveProgressTrack = styled.div`
  ${({ theme }) => css`
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: ${theme.colors.background[40]};
    overflow: hidden;
  `}
`;

export const LiveProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  border-radius: 2px;
  background: linear-gradient(90deg, #22c55e, #4ade80);
  width: ${({ $percent }) => Math.min(100, Math.max(0, $percent))}%;
  transition: width 1s linear;
`;

export const LiveProgressTime = styled.span`
  font-size: 0.625rem;
  font-weight: 600;
  color: #22c55e;
`;

// ── Sidebar: Live Enter Button ──────────────────────────

export const LiveEnterButton = styled(Link)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.625rem;
    margin-top: 0.5rem;
    border-radius: 0.75rem;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    font-size: 0.8125rem;
    font-weight: 700;
    text-decoration: none;
    border: none;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background: ${theme.colors.brand.primary.default};
      color: ${theme.colors.brand.primary.text};
    }
  `}
`;

// ── Sidebar: Live Carousel Dots ─────────────────────────

export const LiveCarouselDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  margin-top: 0.75rem;
`;

export const LiveCarouselDot = styled.button<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '1.25rem' : '0.375rem')};
  height: 0.375rem;
  border-radius: 0.1875rem;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.brand.primary.default : theme.colors.background[40]};
  transition: all 0.3s ease;
`;

// ── Sidebar: Upcoming Cards ─────────────────────────────

export const UpcomingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const UpcomingCard = styled(Link)`
  ${({ theme }) => css`
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.75rem;
    align-items: center;
    text-decoration: none;
    color: inherit;
    transition: all 0.2s ease;
    cursor: pointer;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};

    &:hover {
      border-color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const UpcomingDateBlock = styled.div`
  ${({ theme }) => css`
    min-width: 3rem;
    height: 3rem;
    border-radius: 0.625rem;
    border: 1px solid ${theme.colors.background[40]};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;

    ${UpcomingCard}:hover & {
      border-color: ${theme.colors.brand.primary.default};
      background: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const UpcomingDateDay = styled.span`
  ${({ theme }) => css`
    font-size: 1rem;
    font-weight: 800;
    color: ${theme.colors.foreground[100]};
    line-height: 1.1;

    ${UpcomingCard}:hover & {
      color: #fff;
    }
  `}
`;

export const UpcomingDateMonth = styled.span`
  ${({ theme }) => css`
    font-size: 0.5625rem;
    font-weight: 600;
    color: ${theme.colors.foreground[40]};
    text-transform: uppercase;
    letter-spacing: 0.5px;

    ${UpcomingCard}:hover & {
      color: #fff;
    }
  `}
`;

export const UpcomingInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const UpcomingTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const UpcomingMeta = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    font-size: 0.6875rem;
    font-weight: 500;
    color: ${theme.colors.foreground[40]};

    svg {
      flex-shrink: 0;
    }
  `}
`;

export const UpcomingMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

type StatusPillVariant = 'amber' | 'blue';

export const UpcomingStatusPill = styled.span<{ $variant: StatusPillVariant }>`
  font-size: 0.5625rem;
  font-weight: 700;
  padding: 0.1875rem 0.5rem;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${({ $variant }) =>
    $variant === 'amber'
      ? 'rgba(251, 191, 36, 0.12)'
      : 'rgba(59, 130, 246, 0.12)'};
  color: ${({ $variant }) => ($variant === 'amber' ? '#fbbf24' : '#3b82f6')};
`;

// ── Sidebar: Replay Rows ────────────────────────────────

export const ReplayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

export const ReplayRow = styled(Link)`
  ${({ theme }) => css`
    display: flex;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 0.75rem;
    align-items: center;
    text-decoration: none;
    color: inherit;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      border-color: ${theme.colors.brand.primary.default};
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    }
  `}
`;

export const ReplayThumb = styled.div`
  ${({ theme }) => css`
    width: 3.5rem;
    height: 2.25rem;
    border-radius: 0.375rem;
    background: ${theme.colors.background[60]};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    position: relative;

    svg {
      color: ${theme.colors.foreground[40]};
      z-index: 1;
    }
  `}
`;

export const ReplayThumbImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.4;
`;

export const ReplayInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const ReplayName = styled.div`
  ${({ theme }) => css`
    font-size: 0.75rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const ReplayMeta = styled.div`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 500;
    color: ${theme.colors.foreground[40]};
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `}
`;

// ── Sidebar: No Live (Idle Hero) ────────────────────────

export const IdleHeroWrapper = styled(LiveHeroWrapper)`
  overflow: visible;
  padding: 1.25rem;
`;

export const IdleBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.3125rem 0.75rem;
    border-radius: 1.25rem;
    background: ${theme.colors.background[80]};
    color: ${theme.colors.foreground[40]};
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    margin-bottom: 1rem;
  `}
`;

export const IdleDot = styled.span`
  ${({ theme }) => css`
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: ${theme.colors.foreground[40]};
    opacity: 0.5;
  `}
`;

export const NextUpCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border: 1px solid ${theme.colors.background[40]};
    border-radius: 0.75rem;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
  `}
`;

export const NextUpLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.5625rem;
  font-weight: 700;
  color: #e5a00d;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const NextUpRoomName = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const NextUpSessionTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${theme.colors.foreground[60]};
    margin-top: -0.25rem;
  `}
`;

export const NextUpDateTime = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.6875rem;
    color: ${theme.colors.foreground[40]};
    font-weight: 500;

    > span {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
  `}
`;

export const NextUpCountdown = styled.span`
  color: #e5a00d;
  font-weight: 600;
`;

export const CalendarWrapper = styled.div`
  ${({ theme }) => css`
    margin-top: 0.5rem;
    position: relative;

    .react-add-to-calendar {
      display: block;
      width: 100%;
      position: static;
    }

    .react-add-to-calendar__wrapper {
      display: block;
      width: 100%;
    }

    a.react-add-to-calendar__button,
    a.react-add-to-calendar__button--light {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      width: 100%;
      padding: 0.625rem;
      border-radius: 0.75rem;
      background: ${theme.colors.brand.primary.opaque};
      color: ${theme.colors.brand.primary.default};
      font-size: 0.8125rem;
      font-weight: 700;
      font-family: inherit;
      text-decoration: none;
      border: none;
      cursor: pointer;
      outline: none;
      transition: all 0.2s ease;
    }

    a.react-add-to-calendar__button:hover,
    a.react-add-to-calendar__button--light:hover {
      background: ${theme.colors.brand.primary.default};
      color: ${theme.colors.brand.primary.text};
    }

    .react-add-to-calendar__dropdown {
      position: absolute;
      bottom: 100%;
      left: 0;
      right: 0;
      margin-bottom: 0.375rem;
      z-index: 100;
      background: ${theme.colors.background[100]};
      border: 1px solid ${theme.colors.background[40]};
      border-radius: 0.75rem;
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.12);
    }

    .react-add-to-calendar__dropdown ul {
      list-style: none;
      margin: 0;
      padding: 0.25rem 0;
    }

    .react-add-to-calendar__dropdown ul li a {
      display: block;
      padding: 0.625rem 1rem;
      font-size: 0.8125rem;
      color: ${theme.colors.foreground[80]};
      text-decoration: none;
    }

    .react-add-to-calendar__dropdown ul li a:hover {
      background: ${theme.colors.background[80]};
      color: ${theme.colors.brand.primary.default};
    }
  `}
`;

// ── Sidebar: Bottom Panel (Upcoming + Replays carousel) ─

export const SidebarBottomPanel = styled.div`
  ${({ theme }) => css`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border-top: 1px solid ${theme.colors.background[40]};
    overflow: hidden;
  `}
`;

export const SidebarCarouselNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.75rem 1rem 0;
`;

export const SidebarCarouselDot = styled.button<{ $active: boolean }>`
  ${({ theme, $active }) => css`
    width: ${$active ? '1.25rem' : '0.375rem'};
    height: 0.375rem;
    border-radius: 0.1875rem;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    background: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.background[40]};
    padding: 0;
  `}
`;

export const SidebarCarouselSlide = styled.div<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  overflow-y: auto;
  transition: opacity 0.6s ease-in-out;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 3px;
  }
`;

export const SidebarCarouselTrack = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
`;
